import React, { Component } from 'react';
import moment from 'moment';
import { Box } from 'bloomer';
import { RouteComponentProps } from '@reach/router';
import { Title } from 'bloomer/lib/elements/Title';
import { Container } from 'bloomer/lib/layout/Container';
import LoadingSpinner from '../elements/LoadingSpinner';
import ReactTable from 'react-table';
import api from '../util/api';
import { uniqueOnlyFilter, breakdownEnrichmentTypes, popupErrorFromServer } from '../util/helperFunctions';
import TitleCase from '../elements/TitleCase';
import styled from 'styled-components';

const NewLine = styled.span<any>`
  display: block;
`;

interface Params {
  animalType: animalType;
}

interface PropTypes extends RouteComponentProps<Params> {}
interface StateTypes {
  data: ({
    [Key: string]: any;
  } & {
    name: string;
    aNumber: number;
    total: any;
  })[];
  columns: {
    Header: JSX.Element;
    headerClassName: string;
    className: string;
    accessor: string;
  }[];
  isLoading: boolean;
}

class LiveData extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    data: [],
    columns: [],
    isLoading: true,
  };

  /**
   * Refresh the state by generating enrichment summaries of each animal
   * and building columns for the table
   *
   * @memberof LiveData
   */
  refreshData = async (props: PropTypes) => {
    try {
      this.setState({ isLoading: true });
      const { animalType } = props;
      const todaysEnrichmentData = await api.enrichmentData.get({
        animalType,
        startDate: moment()
          .startOf('day')
          .toISOString(),
        endDate: moment()
          .endOf('day')
          .toISOString(),
      });

      const aNumbers = todaysEnrichmentData
        .map(animal => animal.aNumber) // extract the aNumbers
        .filter(uniqueOnlyFilter); // ensure no duplicate aNumbers

      const data = aNumbers
        .map(aNumber => todaysEnrichmentData.filter(enrich => enrich.aNumber === aNumber)) // get all the enrichments for a given a number
        .map(enrichmentsForOneAnimal => {
          // build an object that sumerizes that animals enrichments by type
          const enrichmentTotalsByType = breakdownEnrichmentTypes(enrichmentsForOneAnimal);
          return Object.assign({}, enrichmentTotalsByType, {
            name: enrichmentsForOneAnimal[0].animalName,
            aNumber: enrichmentsForOneAnimal[0].aNumber,
            total: Object.values(enrichmentTotalsByType).reduce((acc, val) => acc + val, 0),
          });
        });
      const columns = this.createColumns(data);
      this.setState({ data, columns, isLoading: false });
    } catch (err) {
      popupErrorFromServer(err);
    }
  };

  /**
   * Create the column objects for the table using the data provided
   *
   * @memberof LiveData
   */
  createColumns = (
    data: ({
      [Key: string]: any;
    } & {
      name: string;
      aNumber: number;
      total: any;
    })[],
  ) => {
    if (!data.length) {
      return [];
    }
    // Determine columns from data.
    // Flatten data to get array of enrichment types + the strings 'name' and 'aNumber
    const columns = Object.keys(Object.assign({}, ...data))
      .filter(uniqueOnlyFilter)
      .sort((a, b) => {
        // for a given row...

        // aNumber always furthest left
        if (a === 'aNumber') return -1;
        if (b === 'aNumber') return 1;

        // name second
        if (a === 'name') return -1;
        if (b === 'name') return 1;

        // total last
        if (a === 'total') return 1;
        if (b === 'total') return -1;

        // everything else order is unimportant
        return 0;
      })
      .map(columnName => ({
        // make the actual header objects
        Header: (
          <TitleCase>
            {columnName.split(' ').map(word => (
              <NewLine key={`${word}-${columnName}`}>{word}</NewLine>
            ))}
          </TitleCase>
        ),
        headerClassName: 'reactChartHeader is-size-6',
        className: 'is-size-7 centered',
        accessor: columnName,
      }));

    return columns;
  };

  componentDidMount() {
    this.refreshData(this.props);
  }

  componentWillReceiveProps(props: PropTypes) {
    this.refreshData(props);
  }

  render() {
    // Grab data from state but default to [{}] if we have none
    const { data, columns, isLoading } = this.state;
    const { animalType } = this.props;

    let content = (
      <ReactTable
        data={data}
        columns={columns}
        showPagination={false}
        className="-striped -highlight customReactTable"
        defaultPageSize={data.length}
        loading={isLoading}
      />
    );

    if (!isLoading && (!data.length || !columns.length)) {
      // TODO: Replace this with a resuable component
      content = (
        <div className="bulma" style={{ minHeight: '60vh', position: 'relative' }}>
          <p className="is-size-5 centered" style={{ position: 'absolute', top: '50%', width: '100%' }}>
            Sorry! No data to display yet
          </p>
        </div>
      );
    }

    if (isLoading) {
      content = <LoadingSpinner />;
    }

    return (
      <Container>
        <Box>
          <Title>Live Data ({animalType})</Title>
          {content}
        </Box>
      </Container>
    );
  }
}

export default LiveData;
